import React from "react";
import { useClient } from "urql";
import { MlsContext } from "../../mls/context/mls";
import { SearchContext } from "../../common/context/search";
import { classNames } from "../../common/utils/classnames";
import {
  InternalListingBucketsDocument,
  useInternalListingBucketsQuery,
} from "../../graphql/generated";
import { Transition } from "@headlessui/react";
import Loading from "../../common/components/loading";
import Error from "../../common/components/error";
import InternalListingBucketCard from "../components/card";
import InternalListingBucketActions from "../components/actions";
import { useTitle } from "../../common/utils/title";

const InternalListingBucketList: React.FC = () => {
  useTitle("Internal Listing Buckets");
  const graphqlClient = useClient();
  const [loading, setLoading] = React.useState(false);
  const { searchBounced } = React.useContext(SearchContext);
  const { mls } = React.useContext(MlsContext);
  const [active] = React.useState(true);
  const [includeHidden] = React.useState(false);

  const [{ data, error, fetching }] = useInternalListingBucketsQuery({
    variables: {
      first: 20,
      search: searchBounced,
    },
  });

  const fetchMore = React.useCallback(async () => {
    if (data?.internalListingBuckets.pageInfo.hasNextPage) {
      setLoading(true);
      await graphqlClient
        .query(InternalListingBucketsDocument, {
          first: 20,
          after: data.internalListingBuckets.pageInfo.endCursor,
          search: searchBounced,
          mlsId: mls?.id,
          active: active ? active : undefined,
          includeHidden,
        })
        .toPromise();
      setLoading(false);
    }
  }, [active, data, graphqlClient, mls?.id, searchBounced, includeHidden]);

  return (
    <>
      <Loading show={!data && !error} />
      <Error error={error} />
      <Transition
        show={!!data}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-4">
          <div className="sm:flex sm:items-center sm:justify-between">
            <div className="flex-1 min-w-0">
              <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
                Internal Listing Buckets
                <span className="text-sm text-gray-500 px-2">
                  {fetching
                    ? "Loading..."
                    : `${data?.internalListingBuckets.total.toLocaleString()}`}
                </span>
              </h2>
            </div>
            <div className="mt-5 flex sm:mt-0 sm:mr-4">
              <InternalListingBucketActions />
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-4">
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <div className="p-4">
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {data?.internalListingBuckets.edges?.map((bucket) => (
                  <InternalListingBucketCard
                    bucket={bucket?.node}
                    key={bucket?.node.id}
                  />
                ))}
              </div>
            </div>
          </div>
          {data &&
            data?.internalListingBuckets.total >
              (data?.internalListingBuckets.edges?.length ?? 0) && (
              <div className="min-w-full divide-y divide-gray-200">
                <div className="p-4">
                  <button
                    className={classNames(
                      loading
                        ? "bg-gray-500 text-white"
                        : "bg-zenlist-500 hover:bg-zenlist-700 text-white",
                      "font-bold py-2 px-4 rounded",
                    )}
                    onClick={() => fetchMore()}
                    disabled={loading}
                  >
                    Fetch More
                  </button>
                </div>
              </div>
            )}
        </div>
      </Transition>
    </>
  );
};

export default InternalListingBucketList;
